import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateFoundationCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { gla, propertyType } = improvementsForTransaction;

  const foundation =
    propertyType === 'manufacturedHome' ? improvementsForTransaction.foundation : undefined;

  if (!foundation || !gla) return undefined;

  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (!zipCode) return undefined;

  if (foundation === 'pierAndBeam') {
    return {
      totalCost: AloftPrice.zero,
      multiplierBreakdown: {
        costOnly: AloftPrice.zero,
        zipOnly: AloftPrice.zero,
        none: AloftPrice.zero,
      },
      variable: AloftPrice.zero,
      fixed: AloftPrice.zero,
      unit: 'sqft' as const,
    };
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getFoundationCost({
    foundationType: foundation,
    zipCode,
    sqft: AloftArea.schema.parse(gla),
  });
};

const getTotalOrBreakdownFoundationCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateFoundationCost(costProps, improvements, propertyIdentification, id);

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalFoundationCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownFoundationCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
    'totalCost',
  );

const selectFoundationCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (_state, id: string) => id,
  ],
  getTotalFoundationCost,
);

export const selectZipOnlyFoundationCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (_state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownFoundationCost(
      costProps,
      improvements,
      propertyIdentification,
      id,
      'zipOnly',
    ),
);

export const selectFoundationCostOutput = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (_state, id: string) => id,
  ],
  calculateFoundationCost,
);

export const selectFoundationCostDisplay = createSelector(
  [selectFoundationCost],
  (foundationCost) => {
    if (isUndefined(foundationCost)) {
      return undefined;
    }

    return foundationCost.toString(true);
  },
);
